<template>
  <div>
    <a-drawer :width="1040" :visible="visible" :body-style="{ paddingBottom: '80px' }" @close="onClose">
      <span slot="title">
        <span class="title-text">
          {{ title }}
          <span v-if="caseData.key" class="subtitle">#{{ caseData.key }}</span>
        </span>
        <template v-if="isCreate === false">
          <a-space size="middle" class="header-action">
            <ai-score-view :scoring="caseData.scoring" :ai-score="caseData.ai_score" @trigger="aiScoreTrigger" />
            <a-popover placement="bottomLeft">
              <template slot="content">
                <a-tree :tree-data="accInfo" style="width: 300px" />
              </template>
              <a-button v-tc="{ name: '函数链路' }" @click="getCaseAccInfo">函数链路</a-button>
            </a-popover>
            <a-tooltip v-if="isEdit === false" title="点击打开新标签页查看详情">
              <a v-tc="{ name: '打开详情页' }" class="custom-link" :href="link" target="_blank"><a-icon type="fullscreen" /></a>
            </a-tooltip>
            <a-tooltip title="点击切换">
              <a-switch v-model="isEdit" v-tc="{ name: `切换${isEdit === false ? '编辑模式' : '查看模式'}` }" checked-children="编辑模式" un-checked-children="查看模式" />
            </a-tooltip>
          </a-space>
        </template>
      </span>
      <div class="drawer-body" :class="{ loading: loading.detail }">
        <a-spin :spinning="loading.detail" tip="加载中...">
          <case-edit v-if="isEdit === true" ref="caseEditRef" :data-source="caseData" @close="onClose" />
          <case-detail v-else :case-id="caseId" :data-source="caseData" @close="onClose" />
        </a-spin>
      </div>
      <div class="drawer-bottom-menu">
        <a-space>
          <a-button v-if="caseId" v-tc="{ name: '上一条' }" :disabled="isFirst || loading.back || loading.detail" :loading="loading.back" @click="onBack">
            上一条
            <span class="hotkey-hint">(←)</span>
          </a-button>
          <a-button v-if="caseId" v-tc="{ name: '下一条' }" :disabled="isEnd || loading.next || loading.detail" :loading="loading.next" @click="onNext">
            下一条
            <span class="hotkey-hint">(→)</span>
          </a-button>
          <a-button v-tc="{ name: '关闭' }" @click="onClose">
            关闭
            <span class="hotkey-hint">(ESC)</span>
          </a-button>
          <a-button v-if="isCreate" v-tc="{ name: '保存并继续创建' }" :loading="loading.save" @click="onSave('continue')"> 保存并继续创建 </a-button>
          <a-button v-if="isEdit" v-tc="{ name: '保存' }" type="primary" :loading="loading.save" @click="onSave(isCreate ? null : 'continue')"> 保存 </a-button>
        </a-space>
      </div>
    </a-drawer>
  </div>
</template>
<script>
import { caseDetail } from '@/api/project/case';
import { getAccCase, aiScore } from '@/api/project/case';

import CaseEdit from '@/pages/project/case/components/CaseEdit';
import CaseDetail from '@/pages/project/case/components/CaseDetail';
import AiScoreView from '@/pages/project/case/components/AiScoreView';

export default {
  components: { CaseEdit, CaseDetail, AiScoreView },
  data() {
    return {
      caseId: null,
      caseData: {},
      isEdit: false,
      visible: false,
      loading: {
        back: false,
        next: false,
        save: false,
        detail: false
      },
      isFirst: false,
      isEnd: false,
      projectId: parseInt(this.$route.params.project_id) || null,
      accInfo: []
    };
  },
  computed: {
    link() {
      return this.$router.resolve({
        name: 'page:project:quality:case:detail',
        query: { caseId: this.caseId }
      }).href;
    },
    isCreate() {
      return this.caseId === null && this.isEdit === true;
    },
    title() {
      if (this.isCreate) {
        return '添加用例';
      } else if (this.isEdit) {
        return '编辑用例';
      } else {
        return '用例详情';
      }
    }
  },
  mounted() {
    // 组件初始化时不需要获取AccInfo，等到有具体的caseId时再获取
    // 添加键盘事件监听
    document.addEventListener('keydown', this.handleKeyDown);
  },
  beforeDestroy() {
    // 移除键盘事件监听
    document.removeEventListener('keydown', this.handleKeyDown);
  },
  methods: {
    /**
     * 打开抽屉
     * @param {string} id 用例id
     * @param {string} mode 打开模式：create/edit/view
     * @param {boolean} isFirst 是否是第一条
     * @param {boolean} isEnd 是否是最后一条
     */
    onOpen(id, mode, isFirst = false, isEnd = false) {
      this.visible = true;
      this.isEnd = isEnd;
      this.isFirst = isFirst;

      // 重置状态，避免状态污染
      this.caseData = {};

      switch (mode) {
        case 'create':
          this.caseId = null;
          this.isEdit = true;
          break;
        case 'edit':
          this.caseId = id;
          this.isEdit = true;
          this.getCaseDetail();
          break;
        case 'view':
          this.caseId = id;
          // this.isEdit = false; // 明确设置为查看模式
          this.getCaseDetail();
          break;
        default:
          console.warn('Unknown mode:', mode);
          break;
      }
    },
    /**
     * 获取用例详情
     */
    getCaseDetail() {
      if (!this.caseId) return;

      this.loading.detail = true;
      caseDetail({ project_id: this.projectId, id: this.caseId })
        .then(res => {
          this.caseData = res.data;
          // 获取详情成功后，同时获取AccInfo
          this.getCaseAccInfo();
        })
        .catch(error => {
          console.error('Failed to get case detail:', error);
          this.$message.error('获取用例详情失败');
        })
        .finally(() => {
          this.loading.detail = false;
        });
    },
    /**
     * 点击下一条
     */
    async onNext() {
      if (this.isEnd || this.loading.next) return;

      this.loading.next = true;
      try {
        await this.$emit('next', this.caseId);
      } catch (error) {
        console.error('Next operation failed:', error);
        this.$message.error('操作失败，请重试');
      } finally {
        this.loading.next = false;
      }
    },
    /**
     * 点击上一条
     */
    async onBack() {
      if (this.isFirst || this.loading.back) return;

      this.loading.back = true;
      try {
        await this.$emit('back', this.caseId);
      } catch (error) {
        console.error('Back operation failed:', error);
        this.$message.error('操作失败，请重试');
      } finally {
        this.loading.back = false;
      }
    },

    /**
     * 更新导航状态
     * @param {boolean} isFirst 是否是第一条
     * @param {boolean} isEnd 是否是最后一条
     */
    updateNavigationState(isFirst, isEnd) {
      this.isFirst = isFirst;
      this.isEnd = isEnd;
    },

    /**
     * 处理键盘快捷键
     */
    handleKeyDown(event) {
      // 只在抽屉可见时处理快捷键
      if (!this.visible) return;

      // 避免在输入框中触发快捷键
      const tagName = event.target.tagName.toLowerCase();
      if (['input', 'textarea'].includes(tagName) || event.target.contentEditable === 'true') {
        return;
      }

      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          this.onBack();
          break;
        case 'ArrowRight':
          event.preventDefault();
          this.onNext();
          break;
        case 'Escape':
          event.preventDefault();
          this.onClose();
          break;
        default:
          break;
      }
    },
    /**
     * 保存
     */
    async onSave(mode = 'continue') {
      this.loading.save = true;
      const res = await this.$refs.caseEditRef.onSubmit();
      if (res === true && mode !== 'continue') {
        this.visible = false;
      }
      this.loading.save = false;
      this.$emit('callback');
    },

    /**
     * 关闭抽屉
     */
    onClose() {
      // 重置所有状态
      this.caseData = {};
      this.caseId = null;
      this.visible = false;
      this.isEdit = false;
      this.isFirst = false;
      this.isEnd = false;
      this.accInfo = [];

      // 重置加载状态
      Object.keys(this.loading).forEach(key => {
        this.loading[key] = false;
      });

      this.$emit('callback');
    },
    /**
     * 获取case绑定的精准信息
     */
    getCaseAccInfo() {
      if (!this.caseId) {
        this.accInfo = [];
        return;
      }

      getAccCase({ case_id: this.caseId })
        .then(res => {
          this.accInfo = res.data.items || [];
        })
        .catch(error => {
          console.error('Failed to get case acc info:', error);
          this.accInfo = [];
        });
    },
    aiScoreTrigger() {
      aiScore({ case_ids: [this.caseId], project_id: this.projectId })
        .then(res => {
          this.$message.success('AI评分触发成功');
          this.getCaseDetail();
        });
    },
    getScoreClass(score) {
      if (score >= 4) {
        return 'score-high';
      } else if (score >= 2) {
        return 'score-medium';
      } else {
        return 'score-low';
      }
    }
  }
};
</script>

<style lang="less" scoped>
.subtitle {
  margin-left: 8px;
  font-weight: normal;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.45);
}
.header-action {
  float: right;
  margin-right: 28px;
  cursor: pointer;
}
/deep/.ant-drawer-header {
  position: absolute;
  padding: 10px 24px;
  width: 100%;
  right: 0;
  top: 0;
  z-index: 1;
  .ant-drawer-title {
    .title-text {
      // 上下居中不要用flex，因为flex会撑开高度
      display: inline-block;
      height: 32px;
      line-height: 32px;
      vertical-align: middle;
    }
  }
}

.drawer-body {
  margin: 40px 0;
}

/deep/.drawer-bottom-menu {
  position: absolute;
  width: 100%;
  right: 0;
  bottom: 0;
  border-top: 1px solid #e9e9e9;
  padding: 10px 16px;
  background: #fff;
  text-align: right;
  z-index: 1;

  .hotkey-hint {
    color: #999;
    font-size: 11px;
    margin-left: 4px;
    opacity: 0.8;
  }

  .ant-btn:hover .hotkey-hint {
    opacity: 1;
  }
}

// 加载状态下的蒙层效果
.drawer-body {
  position: relative;

  &.loading::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.8);
    z-index: 10;
    pointer-events: none;
  }
}
</style>
